import { useEffect, useMemo, useState } from "react";
import { GetDoc, GetWSMgr } from "@/Utils/StateLib";
import { debounce } from "lodash";
import { isNullish } from "@theorgchartdev/storybook";
import { UpdateUser } from "@/appComponents/Blocks/Modals/SettingsModal/AccountSettings/ManageUsers/Utils";
import { useChartDocumentContext } from "@/providers/ChartDocumentProvider";
import { useTopic } from "./useTopic";
import { Globals } from "@coreapp/CoreApp/Globals";

const initializeUserFavorites = doc => {
  const wsMgr = GetWSMgr();
  const docMgr = wsMgr?.GetDocMgr();
  const profileMgr = docMgr?.GetProfileMgr();
  const viewMgr = doc?.GetViewMgr();
  const defaultFields = profileMgr?.GetDefaultStarFields(doc);
  const visibleFields = viewMgr?.GetFieldList();
  const chartID = doc?.GetChartID();
  if (chartID == null) return;

  const favoriteInfo = wsMgr?.GetFavoriteInfo() ?? {};
  let nextFavoriteInfo = structuredClone(favoriteInfo);

  if (!nextFavoriteInfo.profile) {
    nextFavoriteInfo = { ...nextFavoriteInfo, profile: {} };
  }

  if (!nextFavoriteInfo.profile.fields) {
    nextFavoriteInfo.profile.fields = { [chartID]: defaultFields };
  }

  if (profileMgr.GetEnableStarring() !== true || !nextFavoriteInfo.profile.fields?.[chartID]) {
    nextFavoriteInfo.profile.fields[chartID] = defaultFields;
  }

  let currentShownFavorites = nextFavoriteInfo.profile.fields[chartID];

  return nextFavoriteInfo;
};

const getAdminFavorites = doc => {
  const wsMgr = GetWSMgr();
  const docMgr = wsMgr?.GetDocMgr();
  return docMgr?.GetProfileMgr()?.GetDefaultStarFields(doc);
};

/**
 * @typedef {object} FavoriteController
 * @property {Function} removeStarredField
 * @property {Function} toggleStarredField
 * @property {(* | null)} favorites - The current favorites state.
 * @property {Array<string>} unusedDefaultFavorites - List of default favorite fields not currently starred by the user.
 * @property {boolean} isStarringEnabled - Indicates if starring functionality is enabled.
 * @property {Function} getCurrentFavorites - List of user's starred fields for the current chart.
 * @returns {FavoriteController} An object containing methods and state for managing favorites.
 */
export const useFavorites = props => {
  const [favorites, setFavorites] = useState(null);
  const [defaultFavorites, setDefaultFavorites] = useState(null);
  const { chartDocument } = useChartDocumentContext();

  const loadFavorites = doc => {
    if (!doc || Globals._publishRef) return;
    setFavorites(initializeUserFavorites(doc));
    setDefaultFavorites([...(getAdminFavorites(doc) ?? [])]);
  };

  useEffect(() => {
    loadFavorites(chartDocument.state);
  }, []);

  useTopic("profileLoaded", doc => {
    loadFavorites(doc);
  });

  const unusedDefaultFavorites = useMemo(() => {
    const doc = chartDocument.state;
    if (!doc) return [];
    return (
      defaultFavorites?.filter(
        field => !favorites?.profile.fields[doc.GetChartID()]?.includes(field)
      ) ?? []
    );
  }, [defaultFavorites, favorites, chartDocument.state]);

  const onUpdate = async nextFavorites => {
    const wsMgr = GetWSMgr();
    const doc = GetDoc();
    if (!doc) return;

    const profileMgr = doc.GetProfileMgr();
    const allowUserUpdate = profileMgr.GetEnableStarring();
    if (allowUserUpdate !== true) return;

    let userData = {};
    userData["user"] = [{ favorites: nextFavorites }];
    userData["userID"] = wsMgr.GetUserName();

    const { result } = await UpdateUser({ theData: userData });
    if (result) {
      wsMgr.SetFavoriteInfo(favorites);
      doc.UpdateDocContext();
    }
  };

  const debounceTimer = 100;
  const debouncedSaveFavorites = debounce(onUpdate, debounceTimer);

  const toggleStarredField = fVPair => {
    const fieldName = fVPair?.fieldDef?.GetFieldName();
    const doc = GetDoc();
    const chartId = doc.GetChartID();
    setFavorites(prevFavorites => {
      let nextFavorite = { ...prevFavorites };
      let starredFields = nextFavorite.profile.fields[chartId] ?? [];
      let nextFields;
      if (starredFields.includes(fieldName)) {
        // Remove the field if it's already included
        nextFields = starredFields.filter(item => item !== fieldName);
      } else {
        // Add the field if it's not included
        nextFields = [...starredFields, fieldName];
      }
      nextFavorite.profile.fields[chartId] = nextFields;
      debouncedSaveFavorites(nextFavorite);
      return nextFavorite;
    });
  };

  const removeStarredField = fVPair => {
    const doc = GetDoc();
    const chartId = doc.GetChartID();
    const fieldName = fVPair?.fieldDef?.GetFieldName();
    setFavorites(prevFavorites => {
      let nextFavorite = { ...prevFavorites };
      let starredFields = nextFavorite.profile.fields[chartId] ?? [];
      let nextFields;
      if (isNullish(fieldName) || starredFields.indexOf(fieldName) === -1) return favorites;
      nextFields = starredFields.filter(item => item !== fieldName);
      nextFavorite.profile.fields[chartId] = nextFields;
      debouncedSaveFavorites(nextFavorite);
      return nextFavorite;
    });
  };

  const isStarringEnabled = useMemo(() => {
    const doc = chartDocument.state;
    if (!doc) return false;

    const profileMgr = doc.GetDocMgr().GetProfileMgr();
    return profileMgr.GetEnableStarring() === true;
  }, [chartDocument.state]);

  const getCurrentFavorites = () => {
    const doc = chartDocument.state;
    if (!doc) return [];

    const relevantChartID = props?.chartID ?? doc?.GetChartID();
    return favorites?.profile.fields[relevantChartID] ?? [];
  };

  return {
    removeStarredField,
    toggleStarredField,
    unusedDefaultFavorites,
    isStarringEnabled,
    getCurrentFavorites
  };
};
